import axios, { AxiosResponse } from 'axios';
import {
    ModerationRequest,
    ClientModerationDetails,
    ApprovalRequest,
    RejectionRequest,
    ChangeRequest,
    ModerationStats,
    Contract,
    DigitalSignature,
    PageResponse,
    ApiResponse,
    ModerationFilters,
    UserFrom1C,
    InviteUserRequest,
    InviteUserResponse,
    InviteToken,
    SetPasswordRequest,
    Client,
    ClientFilters
} from '../types/admin.types';

class AdminApiService {
    private baseURL = process.env.REACT_APP_API_URL || '/api';

    constructor() {
        this.setupTokenRefresh();
    }

    // Получение токена из localStorage
    private getAuthHeader() {
        // Сначала проверяем админский токен
        const adminToken = localStorage.getItem('adminToken');
        if (adminToken) {
            return { Authorization: `Bearer ${adminToken}` };
        }

        // Если нет админского токена, используем обычный токен
        const token = localStorage.getItem('accessToken');
        return token ? { Authorization: `Bearer ${token}` } : {};
    }

    // Обновление токенов
    private async refreshTokens(): Promise<boolean> {
        try {
            const refreshToken = localStorage.getItem('refreshToken');
            if (!refreshToken) {
                return false;
            }

            const response = await axios.post(`${this.baseURL}/admin/refresh`, {
                refreshToken: refreshToken
            });

            if (response.data.success) {
                localStorage.setItem('accessToken', response.data.accessToken);
                localStorage.setItem('adminToken', response.data.accessToken);
                localStorage.setItem('refreshToken', response.data.refreshToken);
                return true;
            }
            return false;
        } catch (error) {
            console.error('Ошибка обновления токенов:', error);
            return false;
        }
    }

    // Перехватчик для автоматического обновления токенов
    private setupTokenRefresh() {
        axios.interceptors.response.use(
            (response) => response,
            async (error) => {
                if (error.response?.status === 401) {
                    const refreshed = await this.refreshTokens();
                    if (refreshed) {
                        // Повторяем оригинальный запрос с новым токеном
                        const originalRequest = error.config;
                        originalRequest.headers.Authorization = this.getAuthHeader().Authorization;
                        return axios(originalRequest);
                    } else {
                        // Если не удалось обновить токен, перенаправляем на страницу входа
                        localStorage.clear();
                        window.location.href = '/admin/login';
                    }
                }
                return Promise.reject(error);
            }
        );
    }

    // Admin user info API (используем test endpoint, который работает)
    async getCurrentAdmin(): Promise<AxiosResponse<ApiResponse<any>>> {
        return axios.get(`${this.baseURL}/admin/test`, {
            headers: this.getAuthHeader()
        });
    }

    // Test API
    async testAdmin(): Promise<AxiosResponse<any>> {
        return axios.get(`${this.baseURL}/admin/test`, {
            headers: this.getAuthHeader()
        });
    }

    // Digital Signatures API
    async getContractSignatures(contractId: number): Promise<AxiosResponse<any>> {
        return axios.get(`${this.baseURL}/admin/contracts/${contractId}/signatures`, {
            headers: this.getAuthHeader()
        });
    }

    // Moderation API
    async getModerationQueue(
        filters: ModerationFilters,
        page: number = 0,
        size: number = 20
    ): Promise<AxiosResponse<any>> {
        return axios.get(`${this.baseURL}/admin/moderation/queue`, {
            params: {
                page,
                size,
                status: filters.status,
                searchTerm: filters.searchTerm,
                sortBy: filters.sortBy,
                sortOrder: filters.sortOrder
            },
            headers: this.getAuthHeader()
        });
    }

    async getClientDetails(clientId: number): Promise<AxiosResponse<ClientModerationDetails>> {
        return axios.get(`${this.baseURL}/admin/moderation/requests/${clientId}`, {
            headers: this.getAuthHeader()
        });
    }

    async getModerationRequestDetails(userId: number): Promise<AxiosResponse<any>> {
        return axios.get(`${this.baseURL}/admin/moderation/requests/${userId}`, {
            headers: this.getAuthHeader()
        });
    }

    async approveClient(clientId: number, request: ApprovalRequest): Promise<AxiosResponse<void>> {
        return axios.post(`${this.baseURL}/admin/moderation/clients/${clientId}/approve`, request, {
            headers: this.getAuthHeader()
        });
    }

    async rejectClient(clientId: number, request: RejectionRequest): Promise<AxiosResponse<void>> {
        return axios.post(`${this.baseURL}/admin/moderation/clients/${clientId}/reject`, request, {
            headers: this.getAuthHeader()
        });
    }

    async requestChanges(clientId: number, request: ChangeRequest): Promise<AxiosResponse<void>> {
        return axios.post(`${this.baseURL}/admin/moderation/clients/${clientId}/request-changes`, request, {
            headers: this.getAuthHeader()
        });
    }

    // Contracts API
    async getContractsQueue(
        page: number = 0,
        size: number = 10
    ): Promise<AxiosResponse<PageResponse<Contract>>> {
        return axios.get(`${this.baseURL}/admin/contracts/signing-queue`, {
            params: { page, size },
            headers: this.getAuthHeader()
        });
    }

    async signContract(contractId: number, signature: DigitalSignature): Promise<AxiosResponse<void>> {
        return axios.post(`${this.baseURL}/admin/contracts/${contractId}/sign`, signature, {
            headers: this.getAuthHeader()
        });
    }

    // Reports API
    async getModerationStats(dateRange: [string, string]): Promise<AxiosResponse<ModerationStats>> {
        return axios.get(`${this.baseURL}/admin/reports/moderation-stats`, {
            params: {
                startDate: dateRange[0],
                endDate: dateRange[1]
            },
            headers: this.getAuthHeader()
        });
    }

    async exportRejectedClients(dateRange: [string, string]): Promise<void> {
        const response = await axios.get(`${this.baseURL}/admin/reports/rejected-clients`, {
            params: {
                startDate: dateRange[0],
                endDate: dateRange[1]
            },
            headers: this.getAuthHeader(),
            responseType: 'blob'
        });

        // Скачивание файла
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `rejected-clients-${dateRange[0]}-${dateRange[1]}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    async exportModerationReport(dateRange: [string, string]): Promise<void> {
        const response = await axios.get(`${this.baseURL}/admin/reports/moderation-report`, {
            params: {
                startDate: dateRange[0],
                endDate: dateRange[1]
            },
            headers: this.getAuthHeader(),
            responseType: 'blob'
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `moderation-report-${dateRange[0]}-${dateRange[1]}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    // Notifications API
    async getNotifications(): Promise<AxiosResponse<ApiResponse<any[]>>> {
        return axios.get(`${this.baseURL}/admin/notifications`, {
            headers: this.getAuthHeader()
        });
    }

    async markNotificationAsRead(notificationId: number): Promise<AxiosResponse<void>> {
        return axios.put(`${this.baseURL}/admin/notifications/${notificationId}/read`, {}, {
            headers: this.getAuthHeader()
        });
    }

    // Users Management API
    async getUsersFrom1C(): Promise<AxiosResponse<ApiResponse<UserFrom1C[]>>> {
        return axios.get(`${this.baseURL}/admin/users/1c`, {
            headers: this.getAuthHeader()
        });
    }

    async inviteUser(request: InviteUserRequest): Promise<AxiosResponse<InviteUserResponse>> {
        return axios.post(`${this.baseURL}/admin/users/invite`, request, {
            headers: this.getAuthHeader()
        });
    }

    async getInviteTokens(): Promise<AxiosResponse<ApiResponse<InviteToken[]>>> {
        return axios.get(`${this.baseURL}/admin/users/invite-tokens`, {
            headers: this.getAuthHeader()
        });
    }

    async revokeInviteToken(tokenId: string): Promise<AxiosResponse<void>> {
        return axios.delete(`${this.baseURL}/admin/users/invite-tokens/${tokenId}`, {
            headers: this.getAuthHeader()
        });
    }

    // Public API для установки пароля по токену
    async setPasswordByToken(request: SetPasswordRequest): Promise<AxiosResponse<ApiResponse<void>>> {
        return axios.post(`${this.baseURL}/public/set-password`, request);
    }

    async validateInviteToken(token: string): Promise<AxiosResponse<ApiResponse<InviteToken>>> {
        return axios.get(`${this.baseURL}/public/validate-invite-token/${token}`);
    }

    // Clients API
    async getClients(
        filters: ClientFilters,
        page: number = 0,
        size: number = 20
    ): Promise<AxiosResponse<ApiResponse<PageResponse<Client>>>> {
        return axios.get(`${this.baseURL}/admin/clients`, {
            params: {
                page,
                size,
                status: filters.status,
                searchTerm: filters.searchTerm,
                sortBy: filters.sortBy,
                sortOrder: filters.sortOrder
            },
            headers: this.getAuthHeader()
        });
    }

    // Clients statistics API
    async getClientsStats(): Promise<AxiosResponse<ApiResponse<{
        total: number;
        active: number;
        pending: number;
        rejected: number;
        verified: number;
        blocked: number;
        approved: number;
        needsRevision: number;
        from1C: number;
    }>>> {
        return axios.get(`${this.baseURL}/admin/clients/stats`, {
            headers: this.getAuthHeader()
        });
    }

    // Client blocking API
    async blockClient(clientId: number, reason: string): Promise<AxiosResponse<void>> {
        return axios.post(`${this.baseURL}/admin/clients/${clientId}/block`, { reason }, {
            headers: this.getAuthHeader()
        });
    }

    async unblockClient(clientId: number): Promise<AxiosResponse<void>> {
        return axios.post(`${this.baseURL}/admin/clients/${clientId}/unblock`, {}, {
            headers: this.getAuthHeader()
        });
    }

    async deleteClient(clientId: number): Promise<AxiosResponse<any>> {
        return axios.post(`${this.baseURL}/admin/clients/${clientId}/delete`, {}, {
            headers: this.getAuthHeader()
        });
    }

    // Admin Contracts API
    async getCompaniesWithSignedContracts(
        page: number = 0,
        size: number = 20,
        searchTerm?: string
    ): Promise<AxiosResponse<ApiResponse<any[]>>> {
        return axios.get(`${this.baseURL}/admin/contracts/companies`, {
            params: { page, size, searchTerm },
            headers: this.getAuthHeader()
        });
    }

    async getCompanySignedContracts(
        companyId: number,
        page: number = 0,
        size: number = 20
    ): Promise<AxiosResponse<ApiResponse<any[]>>> {
        return axios.get(`${this.baseURL}/admin/contracts/companies/${companyId}/signed-contracts`, {
            params: { page, size },
            headers: this.getAuthHeader()
        });
    }

    async getSignedContracts(
        page: number = 0,
        size: number = 20,
        companyBin?: string,
        companyName?: string,
        contractNumber?: string
    ): Promise<AxiosResponse<ApiResponse<any[]>>> {
        return axios.get(`${this.baseURL}/admin/contracts/signed`, {
            params: { page, size, companyBin, companyName, contractNumber },
            headers: this.getAuthHeader()
        });
    }

    async getCompanyDetails(companyId: number): Promise<AxiosResponse<ApiResponse<any>>> {
        return axios.get(`${this.baseURL}/admin/contracts/companies/${companyId}`, {
            headers: this.getAuthHeader()
        });
    }

    async getContractStats(): Promise<AxiosResponse<ApiResponse<any>>> {
        return axios.get(`${this.baseURL}/admin/contracts/stats`, {
            headers: this.getAuthHeader()
        });
    }

    // Подписание договора от имени поставщика
    async signContractSupplier(
        contractId: number,
        signatureData: any
    ): Promise<AxiosResponse<ApiResponse<any>>> {
        return axios.post(`${this.baseURL}/admin/contracts/${contractId}/sign-supplier`, signatureData, {
            headers: this.getAuthHeader()
        });
    }

    // Получение данных для подписания
    async getContractSignData(contractId: number): Promise<AxiosResponse<ApiResponse<any>>> {
        return axios.get(`${this.baseURL}/admin/contracts/${contractId}/sign-data`, {
            headers: this.getAuthHeader()
        });
    }

    // Скачивание договора
    async downloadContract(contractId: number): Promise<AxiosResponse<ApiResponse<{ downloadUrl: string }>>> {
        return axios.get(`${this.baseURL}/admin/contracts/${contractId}/download`, {
            headers: this.getAuthHeader()
        });
    }

    // Скачивание CMS архива для договора
    async downloadCmsZip(contractId: number): Promise<Blob> {
        const response = await axios.get(`${this.baseURL}/admin/contracts/${contractId}/download-cms-zip`, {
            headers: this.getAuthHeader(),
            responseType: 'blob'
        });
        return response.data;
    }
}

export const adminApi = new AdminApiService();
export default adminApi;
